/**
 * Thylacin motion layer.
 *
 * Reveals sections as they scroll into view. Three rules keep this from
 * costing more than it gives:
 *
 * 1. The hidden state is gated behind a class this script adds, so if the
 *    script never runs the page is simply static — content is never
 *    stranded invisible.
 * 2. Anything already on screen at load is left alone. Hiding above-the-fold
 *    content would push out LCP and flash the page empty.
 * 3. Only `opacity` and `translate` animate, both compositor-only, and
 *    `translate` is used rather than `transform` so component-level hover
 *    transforms keep working underneath.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches) return;

  const root = document.documentElement;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.03 }
  );

  /* Sections that paint nothing in the scroll flow must never be revealed.
     They are the wrappers around fixed-position UI — the cart drawer, its
     overlay, popups — and hiding one breaks it two ways:

       - `opacity: 0` makes the drawer invisible while it is open, and it only
         clears once the section scrolls into view. The drawer locks body
         scroll when it opens, so the visitor cannot scroll it into view: an
         invisible panel over a page that will not move.
       - `translate` and `will-change` make the wrapper the containing block
         for its own `position: fixed` children, so the drawer resolves
         against the footer instead of the viewport even after it is revealed.

     A zero-height box is the general signal, and it costs nothing to honour:
     a section with no height has no reveal worth animating. `.section-side-cart`
     is named as well because it is the one that broke, and `[data-ty-no-reveal]`
     is the opt-out for anything added later.

     The footer group is named for a different reason: it is one component
     split across several Shopify sections, and revealing each on its own
     made the bands fade in one after another as the page ends -- exactly the
     seams the footer layout works to hide. It renders at rest; the wordmark
     keeps its own reveal. */
  const NO_REVEAL = '.section-side-cart, .shopify-section-group-footer-group, [data-ty-no-reveal]';

  /** @param {Element} el */
  const watch = (el) => {
    if (el.hasAttribute('data-ty-reveal')) return;
    if (el.matches(NO_REVEAL)) return;

    const box = el.getBoundingClientRect();
    if (box.height === 0) return;

    // Leave whatever is already visible untouched (rule 2).
    if (box.top < window.innerHeight * 0.92) return;

    el.setAttribute('data-ty-reveal', '');
    observer.observe(el);
  };

  const scan = (scope = document) => {
    for (const el of scope.querySelectorAll('.shopify-section')) watch(el);
  };

  root.classList.add('ty-motion');
  scan();

  // Sections swapped in by the theme editor or by section rendering.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) scan(event.target.parentElement ?? document);
  });

  // If the visitor turns reduced motion on mid-session, reveal everything.
  reduceMotion.addEventListener('change', (event) => {
    if (!event.matches) return;
    observer.disconnect();
    for (const el of document.querySelectorAll('[data-ty-reveal]')) el.classList.add('is-revealed');
  });
})();
